import { Table } from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

import type { DataTableProps, DataTableRow } from '../types';
import { useDataTable } from '../model/use-data-table';
import { DataTableHeader } from './table-header';
import { DataTableBody } from './table-body';
import { TableToolbar } from './table-toolbar';
import { InfinityScrollLoader } from './infinity-scroll-loader';
import { ColumnCustomizationDialog } from './column-customization-dialog';
import { FullscreenDialog } from './fullscreen-dialog';

export function DataTable<T extends DataTableRow>({
  data,
  height = 'h-[calc(100vh-200px)]',
  columns,
  title,
  tableId,
  isLoading = false,
  isFetchingNextPage = false,
  showToolbar = true,
  hasMore = false,
  onLoadMore,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  actions = [],
  sortField,
  sortDirection,
  onSort,
  getRowId = (row) => row.id,
  enableFullscreen = true,
  enableColumnCustomization = true,
  enableExport = false,
  columnPresets = [],
  hierarchical = false,
  expandedRows,
  onRowExpand,
  getRowChildren,
  getRowLevel,
  renderExpandIcon,
  rowClassName
}: DataTableProps<T>) {
  const {
    table,
    loadMoreRef,
    isCustomizeOpen,
    isFullscreenOpen,
    tempColumnConfigs,
    setIsCustomizeOpen,
    setIsFullscreenOpen,
    reorderColumns,
    toggleColumnVisibility,
    openColumnCustomization,
    saveColumnCustomization,
    cancelColumnCustomization,
    resetColumnCustomization,
    applyPreset,
    rollbackColumnCustomization
  } = useDataTable({
    data,
    columns,
    tableId,
    isFetchingNextPage,
    hasMore,
    onLoadMore,
    selectedIds,
    onSelectionChange,
    getRowId,
    sortField,
    sortDirection,
    onSort
  });

  const renderTableContent = (isInFullscreen = false) => (
    <div className={cn('relative overflow-auto flex-1', isInFullscreen ? 'h-full' : height)}>
      <div className="inline-block min-w-full align-middle px-6 pb-4">
        <Table className="w-full">
          <DataTableHeader table={table} selectable={selectable} hasActions={actions.length > 0} />
          <DataTableBody
            table={table}
            selectable={selectable}
            actions={actions}
            hierarchical={hierarchical}
            expandedRows={expandedRows}
            onRowExpand={onRowExpand}
            getRowChildren={getRowChildren}
            getRowLevel={getRowLevel}
            renderExpandIcon={renderExpandIcon}
            rowClassName={rowClassName}
          />
        </Table>
        {isFullscreenOpen === isInFullscreen && (
          <InfinityScrollLoader loadMoreRef={loadMoreRef} hasMore={hasMore} isLoading={isFetchingNextPage} />
        )}
      </div>

      {isLoading && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center z-10">
          <Spinner className="h-8 w-8" />
        </div>
      )}
    </div>
  );

  return (
    <section className="flex flex-col border border-border shadow-sm rounded-lg bg-card mb-4">
      {showToolbar && (
        <TableToolbar
          title={title}
          enableColumnCustomization={enableColumnCustomization}
          enableExport={enableExport}
          enableFullscreen={enableFullscreen}
          onCustomizeColumns={openColumnCustomization}
          onFullscreen={() => setIsFullscreenOpen(true)}
        />
      )}

      {renderTableContent()}

      <ColumnCustomizationDialog
        open={isCustomizeOpen}
        onOpenChange={setIsCustomizeOpen}
        columnConfigs={tempColumnConfigs}
        onReorderColumns={reorderColumns}
        onToggleColumnVisibility={toggleColumnVisibility}
        onSave={saveColumnCustomization}
        onCancel={cancelColumnCustomization}
        onReset={resetColumnCustomization}
        onRollback={rollbackColumnCustomization}
        onApplyPreset={applyPreset}
        presets={columnPresets}
      />

      <FullscreenDialog open={isFullscreenOpen} onOpenChange={setIsFullscreenOpen} title={title}>
        {renderTableContent(true)}
      </FullscreenDialog>
    </section>
  );
}
